// Seeding the New / Edit Vendor Bill lines from a purchase order.
//
// POS comments item 12: the cost entered on Goods Receive (the supplier's
// invoice price) updates the product's WAC and the 3-way match, but the bill
// form ignored it and pre-filled the PO's gross cost and discount — so the
// agreed price never reached payables. A line for delivered goods now starts
// from what was received, net of the PO discount (Receive pre-fills the net
// cost, so the discount is already in it).

import { getCurrencyDecimals, getSubunitMultiplier } from '@bbpos/utils';
import type { PurchaseOrderItem } from '@/features/purchase-orders';
import { toBillingState, defaultBillQty } from '@/features/vendor-bills';
import type { VendorBill, VendorBillBasis } from '@/features/vendor-bills';

/** 'po' = bills a PO line · 'catalog' = ad-hoc product/ingredient · 'free' = free-text charge (freight…). */
export type LineKind = 'po' | 'catalog' | 'free';

export interface LineRow {
  key: string;
  kind: LineKind;
  po_item_id: string | null;
  product_id: string | null;
  ingredient_id: string | null;
  item_name: string;
  unit: string;
  quantity: string;
  unit_cost: string;   // major units
  gst_rate: string;
  discount_type: 'PERCENTAGE' | 'FLAT';
  discount_value: string;
  /** Free-text charge: add it to the goods' stock cost on post (QA PU4-003). */
  add_to_stock_cost?: boolean;
  ordered?: number;
  received?: number;
  billed?: number;
  /** PO cost per unit after the PO line's discount, minor units — the "vs PO cost" baseline. */
  po_net_unit_cost?: number;
  /** Where the pre-filled cost came from, shown under the line. */
  cost_source?: string;
}

export interface ReceiptLine {
  po_item_id: string;
  quantity: number;
  unit_cost: number;
}

export interface Receipt {
  id: string;
  grn_number: string;
  items: ReceiptLine[];
}

export const newRowKey = () => Math.random().toString(36).slice(2, 10);

/** PO cost per unit after its discount, floored like the API's receive fallback. */
export function poNetUnitCost(item: Pick<PurchaseOrderItem, 'unit_cost' | 'quantity' | 'total_cost' | 'line_discount'>): number {
  return item.line_discount > 0 && item.quantity > 0
    ? Math.floor((item.total_cost - item.line_discount) / item.quantity)
    : item.unit_cost;
}

/**
 * The received cost for one PO line: that receipt's cost when billing one
 * receipt, else the quantity-weighted average over every receipt of the line
 * (exact when they agree). Null when nothing of the line was received.
 */
export function receivedUnitCost(
  poItemId: string,
  receipts: Receipt[],
  grnId?: string,
): { cost: number; source: string } | null {
  const scoped = grnId ? receipts.filter((r) => r.id === grnId) : receipts;
  const lines = scoped.flatMap((r) =>
    r.items.filter((i) => i.po_item_id === poItemId && i.quantity > 0).map((i) => ({ ...i, grn: r.grn_number })),
  );
  if (lines.length === 0) return null;
  if (lines.length === 1) return { cost: lines[0].unit_cost, source: `From ${lines[0].grn}` };
  const qty = lines.reduce((s, l) => s + l.quantity, 0);
  const value = lines.reduce((s, l) => s + l.quantity * l.unit_cost, 0);
  const costs = new Set(lines.map((l) => l.unit_cost));
  return {
    cost: Math.round(value / qty),
    source: costs.size === 1 ? `From ${lines.length} receipts` : `Average of ${lines.length} receipts`,
  };
}

/**
 * Seed the editable rows from the PO (create) or from an existing draft's lines
 * (edit). `grnId` is set when the bill was opened from one receipt.
 */
export function seedRows(
  po: { items: PurchaseOrderItem[]; receiving_history?: Receipt[] },
  basis: VendorBillBasis,
  grnId: string | undefined,
  existing: VendorBill | null,
  /** The PO's currency (any purchase currency). */
  currency: string = 'INR',
): LineRow[] {
  const subunit = getSubunitMultiplier(currency);
  const dp = getCurrencyDecimals(currency);
  const toMajor = (minor: number) => (minor / subunit).toFixed(dp);
  // PERCENTAGE is basis points (÷100 always); FLAT is minor units (÷subunit).
  const discountToInput = (value: number, type: string) =>
    String(type === 'FLAT' ? value / subunit : value / 100);
  const receipts = po.receiving_history ?? [];
  const grn = grnId ? receipts.find((g) => g.id === grnId) : undefined;
  const billLines = new Map((existing?.items ?? []).filter((i) => i.po_item_id).map((i) => [i.po_item_id as string, i]));

  const rows: LineRow[] = po.items.map((item) => {
    const st = toBillingState(item);
    const b = billLines.get(item.id);
    const receiptQty = grn?.items.find((gi) => gi.po_item_id === item.id)?.quantity;
    const qty = b ? b.quantity : defaultBillQty(st, basis, basis === 'RECEIVED' ? receiptQty : undefined);
    const common = {
      key: newRowKey(), kind: 'po' as const, po_item_id: item.id, product_id: null, ingredient_id: null,
      item_name: item.product_name, unit: b?.unit ?? item.unit,
      quantity: qty > 0 ? String(qty) : '',
      // While editing, the bill's own quantity is not "billed" — billed_elsewhere already excludes it.
      ordered: item.quantity, received: item.received_quantity, billed: b ? b.billed_elsewhere : item.billed_quantity ?? 0,
      po_net_unit_cost: poNetUnitCost(item),
    };

    // Editing a draft: its own lines win.
    if (b) {
      return {
        ...common,
        unit_cost: toMajor(b.unit_cost), gst_rate: String(b.gst_rate),
        discount_type: b.discount_type,
        discount_value: b.discount_value ? discountToInput(b.discount_value, b.discount_type) : '',
      };
    }

    // Delivered goods: the cost agreed at receipt, already net of the PO discount.
    const received = basis === 'RECEIVED' ? receivedUnitCost(item.id, receipts, grnId) : null;
    if (received) {
      const poCost = item.line_discount > 0
        ? `PO: ${toMajor(item.unit_cost)} less ${item.discount_type === 'PERCENTAGE' ? `${(item.discount_value ?? 0) / 100}%` : `${toMajor(item.line_discount)} on the line`}`
        : `PO: ${toMajor(item.unit_cost)}`;
      return {
        ...common,
        unit_cost: toMajor(received.cost), gst_rate: String(item.gst_rate ?? 0),
        discount_type: 'PERCENTAGE', discount_value: '',
        cost_source: `${received.source} · ${poCost}`,
      };
    }

    // Full order, or nothing received yet: the PO's cost and discount. A FLAT
    // discount covers the whole ordered line, so a partial bill takes its share
    // (5.000 off 5 units is 1.000 off 1) instead of wiping out the line.
    const discountType = item.discount_type ?? 'PERCENTAGE';
    let discountValue = item.discount_value ?? 0;
    if (discountType === 'FLAT' && discountValue > 0 && item.quantity > 0 && qty > 0 && qty < item.quantity) {
      discountValue = Math.floor((discountValue * qty) / item.quantity);
    }
    return {
      ...common,
      unit_cost: toMajor(item.unit_cost), gst_rate: String(item.gst_rate ?? 0),
      discount_type: discountType,
      discount_value: discountValue ? discountToInput(discountValue, discountType) : '',
    };
  });

  for (const i of (existing?.items ?? []).filter((x) => !x.po_item_id)) {
    rows.push({
      key: newRowKey(), kind: i.product_id || i.ingredient_id ? 'catalog' : 'free', po_item_id: null, product_id: i.product_id, ingredient_id: i.ingredient_id,
      item_name: i.item_name, unit: i.unit ?? '', quantity: String(i.quantity), unit_cost: toMajor(i.unit_cost), gst_rate: String(i.gst_rate),
      discount_type: i.discount_type, discount_value: i.discount_value ? discountToInput(i.discount_value, i.discount_type) : '',
      add_to_stock_cost: i.add_to_stock_cost === true,
    });
  }
  return rows;
}
